import { ChangeDetectionStrategy, Component, ElementRef, viewChild, inject } from '@angular/core';
import { FormControl, ValidatorFn, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { NgClass } from '@angular/common';

export interface INumberCellEditorParams extends ICellEditorParams {
    min?: number;
    max?: number;
    step?: number;
    value: number;
}

@Component({
    template: `
        <input
            [ngClass]="{ 'has-error': numberControl.invalid }"
            #inputElement
            type="number"
            [formControl]="numberControl"
            [step]="params.step ?? null"
            [min]="params.min ?? null"
            [max]="params.max ?? null"
        />
    `,
    styles: [
        `
            :host,
            input {
                width: 100%;
                border: none;
            }
            .has-error {
                color: var(--color-warn);
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ReactiveFormsModule, NgClass]
})
export class NumberCellEditorComponent implements ICellEditorAngularComp {
    private snackbar = inject(MatSnackBar);

    public numberControl = new FormControl<number>(null);
    public params: INumberCellEditorParams;

    readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

    agInit(params: INumberCellEditorParams) {
        this.params = params;
        this.numberControl.setValue(params.value);
        const validators: ValidatorFn[] = [];
        if (typeof params.min === 'number') {
            validators.push(Validators.min(params.min));
        }
        if (typeof params.max === 'number') {
            validators.push(Validators.max(params.max));
        }
        this.numberControl.setValidators(validators);
    }

    afterGuiAttached() {
        this.inputElement().nativeElement.focus();
        this.inputElement().nativeElement.select();
    }

    getValue(): any {
        return this.numberControl.value;
    }

    focusIn() {
        // NYI
    }

    focusOut() {
        // NYI
    }

    isCancelAfterEnd(): boolean {
        const cancel = this.numberControl.pristine || this.numberControl.invalid;
        if (cancel) {
            const { min, max } = this.params;
            let message = `Invalid value provided.`;
            if (typeof min === 'number' && typeof max === 'number') {
                message += ` Must be between ${min} and ${max}`;
            } else if (typeof min === 'number') {
                message += ` Must be above ${min}`;
            } else if (typeof max === 'number') {
                message += ` Must be below ${max}`;
            }
            this.snackbar.open(message, null, { duration: 5000 });
        }
        return cancel;
    }

    isPopup(): boolean {
        return false;
    }

    getPopupPosition(): 'over' | 'under' | undefined {
        return undefined;
    }
}
